import { EmptyState, Spinner, buttonClasses } from '@nexo/ui';
import { Globe, Monitor } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AppVersion } from '../../components/AppVersion';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo } from '../../components/Logo';
import { formatLongDate } from '../../lib/format';
import {
  detectOs,
  PlatformDownloads,
  RecommendedDownload,
  recommendedAsset,
  releaseNotes,
  useLatestRelease,
} from './DownloadOptions';

export function DownloadPage() {
  const { t } = useTranslation();
  const latest = useLatestRelease();
  const os = detectOs();
  const release = latest.data;
  const recommended = recommendedAsset(release, os);
  const notes = release ? releaseNotes(release.notes) : '';

  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-5">
        <Link to="/">
          <Logo />
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact className="text-muted hover:text-text" />
          <Link to="/app" className={buttonClasses({ variant: 'secondary' })}>
            <Globe className="size-4" /> {t('Usar en el navegador')}
          </Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-5 pt-8 pb-20">
        <h1 className="text-4xl font-semibold tracking-tight">{t('Descarga Nexo')}</h1>
        <p className="mt-2 text-lg text-muted">
          {t('Tu equipo, en tu escritorio: Windows, macOS y Linux.')}
        </p>

        {latest.isPending ? (
          <div className="flex justify-center py-20 text-muted">
            <Spinner className="size-6" />
          </div>
        ) : !release ? (
          <EmptyState
            className="mt-10 rounded-2xl border border-border bg-surface"
            icon={<Monitor className="size-8" />}
            title={t('La app de escritorio llega muy pronto')}
            description={t(
              'Mientras tanto puedes usar Nexo en el navegador con todas sus funciones.',
            )}
            action={
              <Link to="/app" className={buttonClasses()}>
                {t('Abrir Nexo')}
              </Link>
            }
          />
        ) : (
          <>
            <section className="mt-8 rounded-2xl border border-border bg-surface p-6 shadow-panel">
              <p className="text-sm text-muted">
                <Trans
                  i18nKey="Versión <strong>{{version}}</strong>"
                  values={{ version: release.version }}
                  components={{ strong: <strong className="text-text" /> }}
                />
                {release.publishedAt && ` · ${formatLongDate(release.publishedAt)}`}
              </p>
              {recommended && os ? (
                <RecommendedDownload asset={recommended} os={os} className="mt-4" />
              ) : (
                <p className="mt-3 text-sm text-muted">{t('Elige tu sistema abajo.')}</p>
              )}
              {notes && (
                <div className="mt-6 border-t border-border pt-4">
                  <h2 className="mb-2 text-sm font-semibold">{t('Novedades')}</h2>
                  <p className="text-sm whitespace-pre-line text-muted">{notes}</p>
                </div>
              )}
            </section>

            <div className="mt-6">
              <PlatformDownloads release={release} os={os} />
            </div>
          </>
        )}
      </main>
      <footer className="py-6 text-center text-xs text-subtle">
        Nexo <AppVersion />
      </footer>
    </div>
  );
}
